import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import type { Project } from "@/types/project";

// Helper function to get badge color based on project type
const getTypeBadgeClass = (type: Project["type"]) => {
  switch (type) {
    case "Indoor Bouldering":
      return "bg-primary text-primary-foreground hover:bg-primary/90";
    case "Outdoor Tower":
      return "bg-emerald-600 text-white hover:bg-emerald-700";
    case "Educational Facility":
      return "bg-amber-500 text-white hover:bg-amber-600";
    case "Training Facility":
      return "bg-slate-600 text-white hover:bg-slate-700";
    default:
      return "bg-primary text-primary-foreground hover:bg-primary/90";
  }
};

interface ProjectModalProps {
  project: Project | null;
  open: boolean;
  onClose: () => void;
}

const ProjectModal = ({ project, open, onClose }: ProjectModalProps) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isImageLoading, setIsImageLoading] = useState(false);

  // Reset image index when project changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [project]);

  // Preload all images when modal opens
  useEffect(() => {
    if (project && open && project.images && project.images.length > 0) {
      project.images.forEach((src) => {
        const img = new Image();
        img.src = src;
      });
    }
  }, [project, open]);

  if (!project) return null;

  const nextImage = () => {
    if (project.images && project.images.length > 0) {
      setIsImageLoading(true);
      setCurrentImageIndex((prev) => (prev + 1) % project.images.length);
    }
  };

  const prevImage = () => {
    if (project.images && project.images.length > 0) {
      setIsImageLoading(true);
      setCurrentImageIndex((prev) => (prev - 1 + project.images.length) % project.images.length);
    }
  };

  const handleImageLoad = () => {
    setIsImageLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-background">
        <DialogHeader>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <Badge className={`mb-4 ${getTypeBadgeClass(project.type)}`}>
                {project.type}
              </Badge>
              {project.highlight && (
                <Badge className="mb-4 ml-2 bg-purple-600 text-white hover:bg-purple-700 text-xs px-2 py-0.5">
                  {project.highlight}
                </Badge>
              )}
              <DialogTitle className="text-3xl font-display text-foreground mb-2">
                {project.name}
              </DialogTitle>
              <DialogDescription className="text-muted-foreground text-lg mb-1">
                {project.location}
              </DialogDescription>
              <div className="flex gap-4 text-sm">
                <span className="text-primary font-semibold">{project.area}</span>
                <span className="text-muted-foreground">{project.year}</span>
              </div>
            </div>
          </div>
        </DialogHeader>

        {/* Image Slider */}
        {project.images && project.images.length > 0 ? (
          <div className="relative my-6 rounded-xl overflow-hidden bg-muted">
            <div className="aspect-video w-full relative">
              {isImageLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-muted z-10">
                  <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              )}
              <img
                key={currentImageIndex}
                src={project.images[currentImageIndex]}
                alt={`${project.name} - ${project.type} climbing facility construction in ${project.location}. Professional bouldering gym and climbing wall installation by Falkors Walls. Image ${currentImageIndex + 1} of ${project.images.length}`}
                className={`w-full h-full object-cover transition-opacity duration-300 ${isImageLoading ? 'opacity-0' : 'opacity-100'}`}
                onLoad={handleImageLoad}
              />
            </div>
            
            {project.images.length > 1 && (
              <>
                {/* Navigation Buttons */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={prevImage}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-background/80 hover:bg-background/90 backdrop-blur-sm"
                >
                  <ChevronLeft className="w-6 h-6" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={nextImage}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-background/80 hover:bg-background/90 backdrop-blur-sm"
                >
                  <ChevronRight className="w-6 h-6" />
                </Button>

                {/* Image Counter */}
                <div className="absolute bottom-4 right-4 bg-background/80 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-medium">
                  {currentImageIndex + 1} / {project.images.length}
                </div>

                {/* Dots Indicator */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                  {project.images.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        index === currentImageIndex
                          ? "bg-primary w-8"
                          : "bg-background/60 hover:bg-background/80"
                      }`}
                      aria-label={`Go to image ${index + 1}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="relative my-6 rounded-xl overflow-hidden bg-gradient-hero">
            <div className="aspect-video w-full flex items-center justify-center">
              <span className="text-white text-4xl font-display">COMING SOON</span>
            </div>
          </div>
        )}

        {/* Description */}
        <div className="my-6">
          <h3 className="text-xl font-display text-foreground mb-3">About the Project</h3>
          <p className="text-muted-foreground leading-relaxed">{project.description}</p>
        </div>

        {/* Key Metrics */}
        <div className="my-6">
          <h3 className="text-xl font-display text-foreground mb-4">Key Metrics</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {project.metrics.map((metric, index) => (
              <div
                key={index}
                className="p-4 bg-muted/50 rounded-lg border border-border"
              >
                <div className="text-sm text-muted-foreground mb-1">{metric.label}</div>
                <div className="text-lg font-semibold text-foreground">{metric.value}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Our Work */}
        <div className="my-6">
          <h3 className="text-xl font-display text-foreground mb-4">Our Work</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {project.ourWork.map((work, index) => (
              <div
                key={index}
                className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg border border-border"
              >
                <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0" />
                <span className="text-foreground">{work}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Website Link */}
        {project.website && (
          <div className="mt-6 pt-6 border-t border-border">
            <a
              href={project.website}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-semibold transition-colors"
            >
              Visit Website →
            </a>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProjectModal;
